import * as yup from 'yup'

// Helper for String validations
function getStringValidations(validations) {
  let schema = yup.string()
  validations.forEach(rule => {
    switch (rule.type) {
      case 'required':
        schema = schema.required(rule.message || 'This field is required')
        break
      case 'min':
        schema = schema.min(
          rule.value,
          rule.message || `Minimum ${rule.value} characters`,
        )
        break
      case 'max':
        schema = schema.max(
          rule.value,
          rule.message || `Maximum ${rule.value} characters`,
        )
        break
      case 'email':
        schema = schema.email(rule.message || 'Invalid email format')
        break
      case 'url':
        schema = schema.url(rule.message || 'Invalid URL')
        break
      case 'matches':
        schema = schema.matches(
          new RegExp(rule.value),
          rule.message || 'Invalid pattern',
        )
        break
      case 'trim':
        schema = schema.trim()
        break
      case 'lowercase':
        schema = schema.lowercase()
        break
      case 'uppercase':
        schema = schema.uppercase()
        break
      case 'typeError':
        schema = schema.typeError(rule.message || 'Invalid type')
        break
      default:
        console.warn(`Unknown string validation type: ${rule.type}`)
    }
  })
  return schema
}

// Helper for Number validations
function getNumberValidations(validations) {
  let schema = yup.number()
  validations.forEach(rule => {
    switch (rule.type) {
      case 'required':
        schema = schema.required(rule.message || 'This field is required')
        break
      case 'min':
        schema = schema.min(
          rule.value,
          rule.message || `Minimum value is ${rule.value}`,
        )
        break
      case 'max':
        schema = schema.max(
          rule.value,
          rule.message || `Maximum value is ${rule.value}`,
        )
        break
      case 'integer':
        schema = schema.integer(rule.message || 'Must be an integer')
        break
      case 'positive':
        schema = schema.positive(rule.message || 'Must be a positive number')
        break
      case 'negative':
        schema = schema.negative(rule.message || 'Must be a negative number')
        break
      case 'moreThan':
        schema = schema.moreThan(
          rule.value,
          rule.message || `Must be more than ${rule.value}`,
        )
        break
      case 'lessThan':
        schema = schema.lessThan(
          rule.value,
          rule.message || `Must be less than ${rule.value}`,
        )
        break
      default:
        console.warn(`Unknown number validation type: ${rule.type}`)
    }
  })
  return schema
}

// Helper for Array validations
function getArrayValidations(validations) {
  let schema = yup.array()
  validations.forEach(rule => {
    switch (rule.type) {
      case 'required':
        schema = schema.required(rule.message || 'This field is required')
        break
      case 'minItems':
        schema = schema.min(
          rule.value,
          rule.message || `At least ${rule.value} items are required`,
        )
        break
      case 'maxItems':
        schema = schema.max(
          rule.value,
          rule.message || `No more than ${rule.value} items are allowed`,
        )
        break
      case 'compact':
        schema = schema.compact()
        break
      default:
        console.warn(`Unknown array validation type: ${rule.type}`)
    }
  })
  return schema
}

// Helper for Boolean validations
function getBooleanValidations(validations) {
  let schema = yup.boolean()
  validations.forEach(rule => {
    if (rule.type === 'required') {
      schema = schema.oneOf([true], rule.message || 'This field is required')
    } else {
      console.warn(`Unknown boolean validation type: ${rule.type}`)
    }
  })
  return schema
}

// Helper for Date validations
function getDateValidations(validations) {
  let schema = yup.date().typeError('Invalid date').nullable('Invalid date')
  validations.forEach(rule => {
    switch (rule.type) {
      case 'required':
        schema = schema.required(rule.message || 'This field is required')
        break
      case 'minDate':
        schema = schema.min(
          new Date(rule.value),
          rule.message || `Date must be after ${rule.value}`,
        )
        break
      case 'maxDate':
        schema = schema.max(
          new Date(rule.value),
          rule.message || `Date must be before ${rule.value}`,
        )
        break
      case 'ageGate':
        schema = schema.test(
          'age-gate',
          rule.message || 'You must be 18 years or older',
          value => {
            const now = new Date()
            const birthDate = new Date(value)
            const age = now.getFullYear() - birthDate.getFullYear()

            // Check if the user is in the same month as their birth month
            if (
              now.getMonth() === birthDate.getMonth() &&
              now.getFullYear() === birthDate.getFullYear() + 18
            ) {
              return false // Prevent users born in the same month 18 years ago
            }

            // Adjust age calculation for months and days
            if (
              now.getMonth() < birthDate.getMonth() ||
              (now.getMonth() === birthDate.getMonth() &&
                now.getDate() < birthDate.getDate())
            ) {
              return age - 1 >= 18
            }

            return age >= 18
          },
        )
        break
      default:
        console.warn(`Unknown date validation type: ${rule.type}`)
    }
  })
  return schema
}

function getYupSchemaForField(yupValidations, fieldType) {
  switch (fieldType) {
    case 'string':
      return getStringValidations(yupValidations)
    case 'number':
      return getNumberValidations(yupValidations)
    case 'array':
      return getArrayValidations(yupValidations)
    case 'boolean':
      return getBooleanValidations(yupValidations)
    case 'date':
      return getDateValidations(yupValidations)
    default:
      console.warn(`Unknown field type: ${fieldType}`)
      return yup.mixed()
  }
}

export function generateDynamicYupSchema(fieldsJson) {
  const shape = {}

  fieldsJson.forEach(field => {
    const { name, yup } = field

    if (yup?.checks && yup?.type) {
      shape[name] = getYupSchemaForField(yup.checks, yup.type)
    }
  })

  return yup.object().shape(shape)
}
